import { EmailSendModal } from "@/components/email/EmailSendModal.tsx";
import { EmailSequenceRunDetails } from "@/components/email/EmailSequenceRunDetails.tsx";
import { EmailViewModal } from "@/components/email/EmailViewModal.tsx";
import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import { EventPreviewModal } from "@/components/event/EventPreviewModal.tsx";
import { FileItem } from "@/components/files/FileItem.tsx";
import {
  useModals,
  Box,
  Button,
  ButtonVariant,
  FontAwesomeIcon,
  HtmlViewer,
  LinkButtonTo,
  Stack,
  Text,
} from "@skailer-com/uikit";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useMentionsHook } from "@/hooks/use-mention.hook.ts";
import i18n from "@/i18n.ts";
import { ScoreCardFeedbackAction } from "@/pages/recruit/candidates/components/ScoreCardFeedbackAction.tsx";
import { OfferRequestViewModal } from "@/pages/recruit/offers/widgets/OfferRequestViewModal.tsx";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiAction, ApiActionTypes } from "@/types/action.types.ts";
import { ApiApprovalRequestStatus } from "@/types/approvals/approval.types.ts";
import { ApiFile } from "@/types/common.types.ts";
import { ApiEmail, ApiEmailStatus } from "@/types/integrations/integration.types.ts";
import { ReactNode, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

export interface ActionItemProps {
  attachments?: ReactNode;
  action: ApiAction;
  dimmed?: boolean;
  actionsSlot?: ReactNode;
}

interface ActionItemCompiled {
  title?: string;
  titleClassName?: string;
  subtitle?: string;
  description?: string;
}

export const ActionItem = ({
  action,
  attachments,
  actionsSlot,
  dimmed = false,
}: ActionItemProps) => {
  const {
    renderEmployeeName,
    renderRelativeDate,
    renderDateAndTime,
    renderDate,
    renderTime,
    stripHtml,
  } = useFormatter();
  const { t } = useTranslation();
  const modalsService = useModals();
  const { replaceMentions } = useMentionsHook();
  const { data: employeeMe } = useEmployeeMe();
  const handleViewEmail = useCallback((message: ApiEmail | undefined = undefined) => {
    if (!message) return;

    analyticsService.trackEvent(analyticEvents.pageAction, {
      [analyticProperties.id]: message.id,
      [analyticProperties.actionType]: "Email Viewed",
    });

    modalsService.openModal(EmailViewModal, {
      email: message,
      onReply: () => {
        modalsService.openModal(EmailSendModal, {
          singleEmail: {
            candidateId: message.candidate_id,
            applicantEmail: message.from_email,
            threadId: message.original_thread_id,
          },
          subject: `Re: ${message.subject}`,
        });
      },
    });
  }, []);

  const comment = useMemo(
    () => replaceMentions(action.comment_data?.text, action.comment_data?.mentioned_employees),
    [action.comment_data]
  );

  const getUserApprovalStatus = useCallback(() => {
    const confirmations = action.offer_request?.approval_request_details?.confirmations || [];
    const userConfirmation = confirmations.find(
      (confirm) => confirm.from_employee?.id === employeeMe?.id
    );

    return {
      isApprover: !!userConfirmation,
      hasPendingApproval: userConfirmation && !userConfirmation.confirmed_at,
      hasApproved: userConfirmation && !!userConfirmation.confirmed_at,
    };
  }, [action.offer_request?.approval_request_details?.confirmations, employeeMe?.id]);

  const titleCompiled = useMemo(() => {
    const result: ActionItemCompiled = {};
    switch (action.type) {
      case ApiActionTypes.event:
        if (action.event) {
          result.title = `${renderDate(action.event.datetime_start)} · ${renderTime(action.event.datetime_start)} — ${renderTime(action.event.datetime_end)}`;
          result.description = action.event.title;
        }

        break;
      case ApiActionTypes.created:
        result.title = `${i18n.t("ats:action_created")} ${action.comment ? `— ${action.comment}` : ""}`;
        break;
      case ApiActionTypes.assigned:
        result.title = action.pipeline_stage?.title ?? i18n.t("ats:action_assigned");
        break;
      case ApiActionTypes.stage_changed:
        result.title = action.pipeline_stage?.title ?? "";
        result.description = comment;
        break;
      case ApiActionTypes.merged:
        result.title = i18n.t("ats:action_merged");
        if (action.merged_applicant)
          result.description = `${action.merged_applicant.first_name} ${action.merged_applicant.last_name}`;
        break;
      case ApiActionTypes.disqualified:
        if (action.reserved === true) {
          result.title =
            i18n.t("ats:action_reserved") + " — " + (action.disqualification_reason?.title ?? "");
        } else {
          result.title =
            i18n.t("ats:action_disqualified") +
            " — " +
            (action.disqualification_reason?.title ?? "");
        }
        result.description = comment;
        break;
      case ApiActionTypes.qualified:
        result.title =
          i18n.t("ats:action_qualified") + " — " + (action.pipeline_stage?.title ?? "");
        result.description = comment;
        break;
      case ApiActionTypes.email_sent:
        if (action.message?.is_outgoing) {
          switch (action.message.status) {
            case ApiEmailStatus.pending:
            case ApiEmailStatus.queued:
              result.title = i18n.t("ats:email_status_pending");
              result.titleClassName = "text-warning";
              break;
            case ApiEmailStatus.canceled:
              result.title = i18n.t("ats:email_status_canceled");
              result.titleClassName = "text-secondary";
              break;
            case ApiEmailStatus.failed:
              result.title = i18n.t("ats:email_status_failed");
              result.titleClassName = "text-danger";
              break;
            default:
              result.title = i18n.t("common:email_sent");
          }
        } else result.title = i18n.t("ats:action_email_candidate_replied");
        result.description = action.message?.subject;
        break;
      case ApiActionTypes.offer_created:
        if (action.offer_request?.offer_data) {
          const isAwaitingApproval =
            action.offer_request?.approval_request_details?.status ===
            ApiApprovalRequestStatus.awaiting;
          const { hasPendingApproval } = getUserApprovalStatus();

          const offerJobTitle = action.offer_request?.offer_data.job_title;
          const isArchived = action.offer_request?.offer_data.is_archived;

          let baseTitle;
          if (isArchived) {
            baseTitle = i18n.t("ats:offer");
          } else if (!isAwaitingApproval) {
            baseTitle = i18n.t("ats:offer");
          } else if (hasPendingApproval) {
            baseTitle = i18n.t("ats:offer_waiting_for_your_approval");
          } else {
            baseTitle = i18n.t("ats:offer_waiting_for_approval");
          }

          const jobTitleSuffix = offerJobTitle ? `: ${offerJobTitle}` : "";
          const archivedSuffix = isArchived ? ` - ${i18n.t("common:deleted")}` : "";

          result.title = `${baseTitle}${jobTitleSuffix}${archivedSuffix}`;
        } else {
          result.title = i18n.t("ats:offer_created");
        }
        break;
      case ApiActionTypes.comment_added:
        result.title = undefined;
        result.description = comment;
        break;
      case ApiActionTypes.cv_added:
        result.title = undefined;
        result.description = t("ats:cv_added", {
          source: action.source?.title ?? "",
        });
        break;
    }
    return result;
  }, [action]);

  const handlePreviewEvent = useCallback((action: ApiAction | undefined) => {
    if (!action?.event) return;

    modalsService.openModal(EventPreviewModal, {
      event: action.event,
    });
  }, []);

  const handlePreviewOffer = useCallback((action: ApiAction | undefined) => {
    if (!action?.offer_request?.approval_request_details) return;

    modalsService.openModal(OfferRequestViewModal, {
      requestId: action.offer_request.approval_request_details.id,
    });
  }, []);

  const offerIcon = useMemo(() => {
    let icon = "";
    let textColor = "";
    let buttonType: keyof ButtonVariant | undefined = undefined;

    if (!action.offer_request?.offer_data.is_archived) {
      switch (action.offer_request?.approval_request_details?.status) {
        case ApiApprovalRequestStatus.approved:
          icon = "fa-circle-check";
          textColor = "text-positive";
          buttonType = "secondary";
          break;
        case ApiApprovalRequestStatus.rejected:
        case ApiApprovalRequestStatus.canceled:
          icon = "fa-circle-xmark";
          textColor = "text-danger";
          buttonType = "secondary";
          break;
        case ApiApprovalRequestStatus.awaiting: {
          const { hasPendingApproval } = getUserApprovalStatus();

          if (hasPendingApproval) {
            icon = "fa-circle-question";
            textColor = "text-warning";
            buttonType = "primary";
          } else {
            icon = "fa-timer";
            textColor = "text-warning";
            buttonType = "secondary";
          }
          break;
        }
        default:
          return null;
      }
    }

    return { icon, textColor, buttonType };
  }, [action, employeeMe]);

  return (
    <Stack className={twMerge("w-full overflow-hidden", dimmed && "pt-3")} gap={"sm"}>
      <Stack gap={"sm"} direction={"horizontal"} items={"center"} className={"overflow-hidden"}>
        {!dimmed && action.author && (
          <Box className={"mt-0.5 flex shrink-0"}>
            <EmployeeAvatar size={"sm"} employee={action.author} />
          </Box>
        )}
        <Stack direction={"horizontal"} gap={"xs"} items={"center"} className={"overflow-hidden"}>
          {action.author && (
            <Text className={"text-secondary text-nowrap text-ellipsis overflow-hidden"}>
              {renderEmployeeName(action.author)}
            </Text>
          )}
          {action.author && <Text className={"text-secondary "}>·</Text>}

          {action.datetime_action_date && (
            <Text
              title={renderDateAndTime(action.datetime_action_date)}
              className={"text-secondary text-nowrap text-ellipsis overflow-hidden"}
            >
              {renderRelativeDate(action.datetime_action_date)}
            </Text>
          )}
        </Stack>
        {actionsSlot && <Box className={"shrink-0"}>{actionsSlot}</Box>}
      </Stack>
      <Box>
        {action.message && action.message.is_unread && (
          <FontAwesomeIcon icon={"fa-solid fa-circle text-new text-sm mr-2"} />
        )}
        {action.event && (
          <LinkButtonTo
            className={twMerge("font-semibold")}
            onClick={() => {
              handlePreviewEvent(action);
            }}
          >
            {titleCompiled.title}
          </LinkButtonTo>
        )}
        {action.feedback && <ScoreCardFeedbackAction feedback={action.feedback} dimmed={dimmed} />}
        {action.offer_request ? (
          action.offer_request.offer_data.is_archived ? (
            <Text className={"text-title line-through"}>{titleCompiled.title}</Text>
          ) : (
            <Button
              className={"font-light max-w-full"}
              leftIcon={
                <FontAwesomeIcon
                  icon={`fa-light ${offerIcon?.icon || ""}`}
                  className={offerIcon?.textColor || ""}
                />
              }
              variant={offerIcon?.buttonType}
              onClick={() => {
                handlePreviewOffer(action);
              }}
              disabled={!action.offer_request.acl.can_view}
            >
              {titleCompiled.title}
            </Button>
          )
        ) : null}
        {!action.event && !action.offer_request && titleCompiled.title && (
          <Text
            className={twMerge(
              dimmed ? "text-default" : "text-title",
              titleCompiled.titleClassName
            )}
          >
            {titleCompiled.title}
          </Text>
        )}
        {titleCompiled.title && titleCompiled.description && (
          <Text className={"text-secondary mx-2"}>·</Text>
        )}
        {titleCompiled.description && (
          <Text
            className={twMerge(
              "text-default whitespace-pre-wrap overflow-hidden gap-xs",
              dimmed ? "line-clamp-1 text-ellipsis" : undefined
            )}
          >
            {dimmed ? (
              <Box className={"whitespace-pre-line"}>{stripHtml(titleCompiled.description)}</Box>
            ) : (
              <HtmlViewer content={titleCompiled.description} maxLines={10} />
            )}

            {/*space between text and button*/}
            {action.type == ApiActionTypes.email_sent && (
              <LinkButtonTo
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  handleViewEmail(action.message);
                }}
              >
                {t("common:view")}
              </LinkButtonTo>
            )}
          </Text>
        )}
        {titleCompiled.subtitle && (
          <Text className={"text-secondary block"}>{titleCompiled.subtitle}</Text>
        )}
      </Box>

      {action.message?.sequence_run && (
        <EmailSequenceRunDetails
          message={{ ...action.message, sequence_run: action.message.sequence_run }}
        />
      )}

      {action.comment_data?.files && action.comment_data.files.length > 0 && (
        <Box className={"overflow-hidden"}>
          <Stack gap={"xs"} className={"overflow-hidden"}>
            {action.comment_data?.files.map((file: ApiFile) => (
              <Box className={"flex"} key={`comment_file${file.id}`}>
                <FileItem file={file} />
              </Box>
            ))}
          </Stack>
        </Box>
      )}
      {attachments}
    </Stack>
  );
};
